import {api} from './api';
import {Tenant, TenantBiotwinCount, TenantInvitation, TenantPatientInfo, TenantUser} from '../../features/tenant/types';
import {buildURLSearch} from '../utils/apiUtils';
import {MatchMode, OrderDirection} from '../types';

interface BulkRegisterResponse {
  message: {
    correlationId: string;
    success: boolean;
    message: string;
    successfulEmails: string[];
    failedRegistrations: {
      email: string;
      isSuccess: boolean;
      failureReason: string;
    }[];
  };
}

export const tenantApi = api.injectEndpoints({
  endpoints: (builder) => ({
    deleteTenantUser: builder.mutation<void, {tenantId: number; userId: number}>({
      query: (arg) => ({url: `/tenants/${arg.tenantId}/users/${arg.userId}`, method: 'DELETE'}),
      onQueryStarted: async (arg, {dispatch, queryFulfilled}) => {
        try {
          await queryFulfilled;
          await dispatch(api.util.invalidateTags(['TenantUsers']));
        } catch (e) {}
      },
    }),

    getTenantUsers: builder.query<
      {items: TenantUser[]; totalCount: number},
      {
        isBiotype?: boolean;
        name?: string;
        nameMatchMode?: MatchMode;
        orderDirection?: OrderDirection;
        orderPropertyName?: unknown;
        pageNumber?: number;
        pageSize?: number;
        tenantId?: number;
      }
    >({
      query: (arg) => ({url: `/tenants/${arg.tenantId}/users${buildURLSearch(arg)}`, method: 'GET'}),
      providesTags: ['TenantUsers'],
    }),

    getTenants: builder.query<Tenant[], void>({
      query: () => ({url: '/tenants/user', method: 'GET'}),
    }),

    getAllTenants: builder.query<Tenant[], void>({
      query: () => ({url: '/tenants', method: 'GET'}),
    }),

    createTenantInvitation: builder.mutation<TenantInvitation, {email: string; message: string; tenantId: number}>({
      query: (arg) => ({url: `/tenants/${arg.tenantId}/invitations`, method: 'POST', body: arg}),
      onQueryStarted: async (arg, {dispatch, queryFulfilled}) => {
        try {
          const {data: tenantInvitation} = await queryFulfilled;
          await dispatch(
            tenantApi.util.updateQueryData('getTenantInvitations', {tenantId: arg.tenantId}, (data) => {
              return [...(data ?? []), tenantInvitation];
            }),
          );
        } catch (e) {}
      },
    }),

    deleteTenantInvitation: builder.mutation<void, {id: number; tenantId: number}>({
      query: (arg) => ({url: `/tenants/${arg.tenantId}/invitations/${arg.id}`, method: 'DELETE'}),
      onQueryStarted: async (arg, {dispatch, queryFulfilled}) => {
        try {
          await queryFulfilled;
          await dispatch(
            tenantApi.util.updateQueryData('getTenantInvitations', {tenantId: arg.tenantId}, (data) => {
              const index = data?.findIndex((tenantInvitation) => tenantInvitation.id === arg.id) ?? -1;
              if (index !== -1) {
                data.splice(index, 1);
              }
            }),
          );
        } catch (e) {}
      },
    }),

    getTenantInvitations: builder.query<TenantInvitation[], {tenantId: number}>({
      query: (arg) => ({url: `/tenants/${arg.tenantId}/invitations`, method: 'GET'}),
      providesTags: ['TenantInvitations'],
    }),

    updateTenantInvitation: builder.mutation<TenantInvitation, {id: number; tenantId: number; message?: string}>({
      query: (arg) => ({
        url: `/tenants/${arg.tenantId}/invitations/${arg.id}`,
        method: 'PUT',
        body: {message: arg.message},
      }),
      onQueryStarted: async (arg, {dispatch, queryFulfilled}) => {
        try {
          const {data: tenantInvitation} = await queryFulfilled;
          await dispatch(
            tenantApi.util.updateQueryData('getTenantInvitations', {tenantId: arg.tenantId}, (data) => {
              const index = data?.findIndex((ti) => ti.id === arg.id) ?? -1;
              if (index !== -1) {
                data.splice(index, 1, tenantInvitation);
              }
            }),
          );
        } catch (e) {}
      },
    }),

    getTenantBiotwinCounts: builder.query<TenantBiotwinCount[], {tenantId: number}>({
      query: (arg) => ({url: `/tenants/${arg.tenantId}/biotype-counts`, method: 'GET'}),
    }),

    bulkRegisterUsers: builder.mutation<BulkRegisterResponse, {tenantId: string; emails: string[]}>({
      query: ({tenantId, emails}) => ({
        url: `/identities/ldap-bulk-register/${tenantId}`,
        method: 'POST',
        body: emails,
      }),
      invalidatesTags: ['TenantUsers'],
    }),

    getTenantPatientInfo: builder.query<TenantPatientInfo, {patientId: number | string; tenantId: number}>({
      query: (arg) => ({url: `/tenants/${arg.tenantId}/patient-info/${arg.patientId}`, method: 'GET'}),
    }),

    updateTenantPatientInfo: builder.mutation<void, {patientId: number | string; tenantId: number; data: boolean}>({
      query: (arg) => ({
        url: `/tenants/${arg.tenantId}/patient-info/${arg.patientId}/processed`,
        method: 'PUT',
        body: arg.data,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      onQueryStarted: async (arg, {dispatch, queryFulfilled}) => {
        try {
          await queryFulfilled;
          dispatch(
            tenantApi.util.updateQueryData(
              'getTenantPatientInfo',
              {tenantId: arg.tenantId, patientId: arg.patientId},
              (data) => {
                if (data) {
                  data.isAllProcessed = arg.data;
                  data.items.forEach((item) => {
                    item.isProcessed = arg.data;
                  });
                }
              },
            ),
          );
        } catch (e) {}
      },
    }),
  }),
});

export const {
  useDeleteTenantUserMutation,
  useGetTenantUsersQuery,
  useLazyGetTenantUsersQuery,

  useGetTenantsQuery,
  useGetAllTenantsQuery,

  useCreateTenantInvitationMutation,
  useDeleteTenantInvitationMutation,
  useGetTenantInvitationsQuery,
  useUpdateTenantInvitationMutation,

  useGetTenantBiotwinCountsQuery,
  useBulkRegisterUsersMutation,

  useGetTenantPatientInfoQuery,
  useUpdateTenantPatientInfoMutation,
} = tenantApi;
